// Stuck: work that cannot move until someone acts (today's Stuck, on the new
// server's records): a session that failed, a check that failed, a review
// that sent the work back or ended without a verdict, a review that waits
// for a reviewer, a head or a merge the queue refused. An issue
// already landed or finished has none, and the other summaries win over it:
// work that needs you, is ready to merge or is being worked on is not stuck.
import type { Issue, Proposal, ProposalEnded, Session } from '../api/types'
import { endedText } from '../sessions/words'

const LANDED = new Set<Issue['state']>(['on_main', 'done', 'cancelled', 'duplicate'])

/** The ways a proposal went back that are no one's decision: the rest (replaced, repository changed, a person's note) are not failures. */
const FAILURES: ReadonlySet<ProposalEnded['kind']> = new Set([
  'head_not_on_forge', 'head_not_pushed', 'check_failed', 'check_not_run', 'reviewed', 'review_not_given',
  'blocked', 'gave_up', 'candidate_refused', 'candidate_not_built',
])

const clean = (text: string, most = 60) => text.replace(/[\p{Cc}\p{Cf}]/gu, ' ').slice(0, most)

/** Why a returned proposal is stuck, in a few words. */
export function endedWhy(ended: ProposalEnded): string | null {
  switch (ended.kind) {
    case 'check_failed': return ended.parts.length ? `Check failed · ${clean(ended.parts.join(', '))}` : 'Check failed'
    case 'check_not_run': return 'Check did not run'
    case 'reviewed': return 'Review sent it back'
    case 'review_not_given': return 'Review not given'
    case 'head_not_on_forge': case 'head_not_pushed': return 'Head not pushed'
    case 'blocked': case 'gave_up': case 'candidate_refused': case 'candidate_not_built': return 'Merge refused'
    default: return null
  }
}

/** The review sessions the queue started for `proposal`, oldest first (Poulpe-495). */
export function reviewsOf(proposal: Proposal, sessions: Iterable<Session>): Session[] {
  return [...sessions].filter(s => s.origin.kind === 'review' && s.origin.proposal === proposal.id)
    .sort((a, b) => (a.created_at === b.created_at ? (a.id < b.id ? -1 : 1) : a.created_at < b.created_at ? -1 : 1))
}

/**
 * How a review session ended, in a few words: its outcome and the runner's
 * reason, then why its limits stopped it, if they did; none while it has
 * neither (Poulpe-495).
 */
export function reviewEndText(session: Session): string | null {
  const stopped = session.allowance?.stopped?.trim()
  const ended = session.ended ? endedText(session.ended) : null
  return ended && stopped ? `${ended} · ${stopped}` : ended ?? (stopped || null)
}

const newer = (a: Proposal, b: Proposal) => (a.created_at === b.created_at ? (a.id > b.id ? 1 : -1) : a.created_at > b.created_at ? 1 : -1)

/**
 * Why `issue` is stuck, if it is: its newest proposal came back for a
 * failure (a review not given says how its last reviewer ended), is open
 * with a failed check, or waits at its review for a reviewer, saying why;
 * else its newest session failed and no session of it is alive. An issue that is landed or finished is not.
 * Whether it is also needing you, ready or at work is the summary's to say.
 */
export function stuckWhy(issue: Issue, sessions: Iterable<Session>, proposals: Iterable<Proposal>, returned: Iterable<Proposal>): string | null {
  if (LANDED.has(issue.state)) return null
  const all = [...sessions]
  const latest = [...proposals, ...returned].filter(p => p.issue_id === issue.id).sort(newer).at(-1)
  if (latest?.state === 'returned' && latest.ended && FAILURES.has(latest.ended.kind)) {
    const last = latest.ended.kind === 'review_not_given' ? reviewsOf(latest, all).at(-1) : undefined
    const how = last ? reviewEndText(last) : null
    return how ? `Review not given · ${clean(how, 200)}` : endedWhy(latest.ended)
  }
  if (latest?.state === 'open' && latest.check_output.length > 0) {
    const parts = latest.check_output.map(o => o.part).join(', ')
    return parts ? `Check failed · ${clean(parts)}` : 'Check failed'
  }
  // Its full reason is in the line's title and in Peek.
  if (latest?.state === 'open' && latest.step === 'review' && !latest.review && latest.review_waits) return `Review waits · ${clean(latest.review_waits, 200)}`
  const own = all.filter(s => s.issue_id === issue.id)
  if (own.some(s => s.state !== 'ended')) return null
  const last = own.sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : a.id < b.id ? -1 : 1)).at(-1)
  return last?.ended?.outcome === 'failed' ? 'Session failed' : null
}
